/**
 * Everything the log screen shows for one baby, derived from the entry list.
 * Pure so the numbers and strings are unit-tested against the design's mock
 * day. "Last …" looks across the whole fetched window (at 1am you still want
 * last night's 11pm bottle); "today" totals use the phone's local day.
 */
import { type BottleUnit, roundOz, snapOz, toOz, USUAL_BOTTLE } from "@babia/checks";
import { type EntryLike, entryDetail } from "./entry-text.ts";
import { ago, agoText, clock, isSameLocalDay, median, weekdayDay } from "./time.ts";

const DAY_MS = 24 * 60 * 60 * 1000;

export interface LogStats {
  /** This baby's entries, newest first. */
  mine: EntryLike[];
  /** Entries in the local day of `now`, plus any open sleep. Newest first. */
  today: EntryLike[];
  /** Entries before local midnight, newest first. */
  earlier: EntryLike[];
  lastFeed: EntryLike | null;
  lastDiaper: EntryLike | null;
  lastMeds: EntryLike | null;
  openSleep: EntryLike | null;
  /** When the last finished sleep ended: "awake since". */
  lastWake: Date | null;
  /** The unit the household logs bottles in; `todayOz` and `usualOz` are stored ounces to show in it. */
  unit: BottleUnit;
  todayOz: number;
  todayFeeds: number;
  todayDiapers: number;
  /**
   * The median bottle of the last 24 hours, snapped to the unit's step (the
   * nearest half ounce, or 10 ml), else the unit's usual bottle, in stored
   * ounces. The median so, while most bottles are one size, a top-up or a
   * typo doesn't drag it off that size; a rolling day, not today, so it
   * carries across midnight.
   */
  usualOz: number;
  // Display strings (the design's renderVals)
  lastFeedAgo: string;
  lastFeedDetail: string;
  diaperSub: string;
  medsSub: string;
  /**
   * The sleep tile names the event a tap records, never a state or a command:
   * "Asleep" read as status and "Wake up" as an instruction, which confused.
   */
  sleepLabel: "Fell asleep" | "Woke up";
  /** The state right now: "Awake 45 min", "Asleep 2 hr 6 min". */
  sleepSub: string;
}

export function deriveStats(
  entries: readonly EntryLike[],
  babyId: string,
  now: Date,
  unit: BottleUnit = "oz",
): LogStats {
  const mine = entries
    .filter((e) => e.babyId === babyId)
    .sort((a, b) => b.at.getTime() - a.at.getTime());
  const today = mine.filter(
    (e) => isSameLocalDay(e.at, now) || (e.type === "sleep" && e.endedAt === null),
  );
  const earlier = mine.filter((e) => !today.includes(e));
  const feeds = today.filter((e) => e.type === "feed");
  const todayOz = roundOz(feeds.reduce((n, e) => n + (e.amountOz ?? 0), 0));
  const usual = median(
    mine
      .filter((e) => e.type === "feed" && now.getTime() - e.at.getTime() < DAY_MS)
      .map((e) => e.amountOz ?? 0),
  );
  const lastFeed = mine.find((e) => e.type === "feed") ?? null;
  const lastDiaper = mine.find((e) => e.type === "diaper") ?? null;
  const lastMeds = mine.find((e) => e.type === "meds") ?? null;
  const openSleep = mine.find((e) => e.type === "sleep" && e.endedAt === null) ?? null;
  const lastWake =
    mine
      .filter((e) => e.type === "sleep" && e.endedAt !== null)
      .map((e) => e.endedAt!)
      .sort((a, b) => b.getTime() - a.getTime())[0] ?? null;

  return {
    mine,
    today,
    earlier,
    lastFeed,
    lastDiaper,
    lastMeds,
    openSleep,
    lastWake,
    unit,
    todayOz,
    todayFeeds: feeds.length,
    todayDiapers: today.filter((e) => e.type === "diaper").length,
    usualOz: usual === null ? toOz(USUAL_BOTTLE[unit], unit) : snapOz(usual, unit),
    lastFeedAgo: lastFeed ? agoText(lastFeed.at, now) : "—",
    lastFeedDetail: lastFeed
      ? `${entryDetail(lastFeed, unit)} at ${clock(lastFeed.at)}`
      : "No bottle logged yet",
    diaperSub: lastDiaper
      ? `${agoText(lastDiaper.at, now)} · ${entryDetail(lastDiaper, unit)}`
      : "None yet",
    // Past a day, "30 hr 58 min ago" is harder to read than the day it was.
    medsSub: lastMeds
      ? now.getTime() - lastMeds.at.getTime() < DAY_MS
        ? agoText(lastMeds.at, now)
        : `Last given ${weekdayDay(lastMeds.at)}`
      : "None yet",
    sleepLabel: openSleep ? "Woke up" : "Fell asleep",
    sleepSub: openSleep
      ? `Asleep ${ago(openSleep.at, now)}`
      : lastWake
        ? `Awake ${ago(lastWake, now)}`
        : "Tap when they fall asleep",
  };
}
